import { cn } from '@utazzo/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Horizontal snap carousel with the small pill-shaped ‹ › control from the
 * design. Native scroll (touch swipe on phones), arrows only when the row
 * actually overflows.
 */
export const HScroller = ({
  children,
  className,
  label,
  alwaysShowArrows = false,
  arrows = 'pill',
  viewportClassName,
}: {
  children: React.ReactNode;
  className?: string;
  label: string;
  /** Keep the ‹ › control visible (disabled at the ends) even when nothing overflows. */
  alwaysShowArrows?: boolean;
  /**
   * `pill`: the small ‹ › pill above the row, right-aligned (the design's).
   * `side`: round buttons either side of the row, which then pages a full
   * view at a time - for a capped row like the destination circles.
   */
  arrows?: 'pill' | 'side';
  /** Extra classes for the scrolling row itself, e.g. a max width that caps how many items show. */
  viewportClassName?: string;
}): React.JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft <= 4;
    const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, children]);

  const scroll = (dir: 1 | -1) => {
    const el = ref.current;
    // Side arrows page a whole view (the next set of circles); snap lines it up.
    const step = arrows === 'side' ? el?.clientWidth : (el?.clientWidth ?? 0) * 0.8;
    if (el && step) el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const overflowing = !(edges.start && edges.end);

  const viewport = (
    <div
      ref={ref}
      onScroll={measure}
      className={cn(
        'flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-1 @xl:gap-4',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        viewportClassName,
      )}
    >
      {children}
    </div>
  );

  if (arrows === 'side') {
    const showArrows = overflowing || alwaysShowArrows;
    const sideBtn =
      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#093F51]/30 bg-white text-[#093F51] shadow-sm transition hover:bg-[#E0F9FC] disabled:pointer-events-none disabled:opacity-30';
    return (
      <div className={cn('flex items-center gap-2 @xl:gap-3', className)}>
        {showArrows && (
          <button
            type="button"
            aria-label={`Previous ${label}`}
            onClick={() => scroll(-1)}
            disabled={edges.start}
            // Nudged up by half the name line, so it centres on the circles.
            className={cn(sideBtn, '-mt-5')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">{viewport}</div>
        {showArrows && (
          <button
            type="button"
            aria-label={`Next ${label}`}
            onClick={() => scroll(1)}
            disabled={edges.end}
            className={cn(sideBtn, '-mt-5')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {(overflowing || alwaysShowArrows) && (
        <div className="mb-2 flex justify-end">
          <div className="inline-flex overflow-hidden rounded-full border border-[#093F51]/40 bg-white">
            <button
              type="button"
              aria-label={`Previous ${label}`}
              onClick={() => scroll(-1)}
              disabled={edges.start}
              className="px-1.5 py-0.5 text-[#093F51] transition hover:bg-[#E0F9FC] disabled:opacity-30"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="w-px bg-[#093F51]/30" />
            <button
              type="button"
              aria-label={`Next ${label}`}
              onClick={() => scroll(1)}
              disabled={edges.end}
              className="px-1.5 py-0.5 text-[#093F51] transition hover:bg-[#E0F9FC] disabled:opacity-30"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      {viewport}
    </div>
  );
};
